import { useEffect, useState } from "react";

/** The 'useOneTrustCookiePolicy' hook has been added so we can hook into oneTrust's functionality for the following reason:
 *
 * 1 - Without this, the cookie policy list won't be rendered unless you refresh the page
 *
 */
export const loadOneTrustCookiePolicy = () => {
	try {
		const allowedPages = ["/privacy/cookies"];

		if (!allowedPages.includes(window.location.pathname)) {
			console.log(
				"No cookie policy on this page so we don't need to initialise the OneTrust cookie policy list",
			);
			return;
		}

		let intervalId: number;
		const maxAttempts = 5;
		let attemptCount = 0;

		const initializeCookiesList = () => {
			if (attemptCount >= maxAttempts) return;
			const element = document.getElementById("ot-sdk-cookie-policy");

			if (
				element &&
				window.OneTrust &&
				window.OneTrust.initializeCookiePolicyHtml
			) {
				console.log("Initialising the OneTrust cookie policy list");
				window.OneTrust.initializeCookiePolicyHtml(element);
				clearInterval(intervalId);
			} else {
				attemptCount++;
			}
		};

		intervalId = setInterval(initializeCookiesList, 500) as unknown as number;
		return () => clearInterval(intervalId);
	} catch (error) {
		console.error(
			"Failed to initialise the OneTrust cookie policy list",
			error,
		);
	}
};

/** The 'loadOneTrustCookiePreferencesCentre' hook has been added so we can hook into oneTrust's functionality for the following reason:
 *
 * 1 - Without this, the footer's cookie preferences link won't work unless you refresh the page
 *
 */
export const loadOneTrustCookiePreferencesCentre = () => {
	try {
		let intervalId: number;
		const maxAttempts = 5;
		let attemptCount = 0;

		const initialiseCookiePreferencesButton = () => {
			if (attemptCount >= maxAttempts) return;
			const element = document.getElementById("ot-sdk-btn");

			if (element) {
				element.addEventListener("click", () => {
					window.OneTrust.ToggleInfoDisplay();
				});
				console.log(
					"Initialising the OneTrust cookie preference centre toggle functionality",
				);
				clearInterval(intervalId);
			} else {
				attemptCount++;
			}
		};

		intervalId = setInterval(
			initialiseCookiePreferencesButton,
			500,
		) as unknown as number;
		return () => clearInterval(intervalId);
	} catch (error) {
		console.error(
			"Failed to enable the OneTrust cookie preference centre toggle functionality",
			error,
		);
	}
};

/** The 'useOneTrustFocusWrapper' hook has been added so we can hook into oneTrust's functionality for the following reasons:
 *
 * 1 - Without this, screen readers on iOS are placed in the footer after closing the cookie notice
 *
 * 2 - With this, the page focus is shifted to the top of the page
 *
 */
export const useOneTrustFocusWrapper = () => {
	try {
		const [hasClosedInitialCookieBanner, setHasClosedInitialCookieBanner] =
			useState(false);
		const [hasOneTrustLoaded, setHasOneTrustLoaded] = useState(false);

		// Wait until OneTrust has loaded (so we have access to its properties)
		useEffect(() => {
			const interval = setInterval(() => {
				if (window.OneTrust) {
					setHasOneTrustLoaded(true);
					clearInterval(interval);
				}
			}, 500);
			return () => clearInterval(interval);
		}, []);

		useEffect(() => {
			if (typeof window === "undefined" || !window.OneTrust) {
				console.log(
					"OneTrust isn't available so we can't run our shift focus hook",
				);
				return;
			}
			if (!hasClosedInitialCookieBanner && hasOneTrustLoaded) {
				window.OneTrust.OnConsentChanged(() => {
					document.getElementById("gatsby-focus-wrapper").focus();
				});
				setHasClosedInitialCookieBanner(true);
			}
		}, [hasClosedInitialCookieBanner, hasOneTrustLoaded]);

		return hasClosedInitialCookieBanner;
	} catch (error) {
		console.error(
			"Failed to shift focus for screen readers to the top of the page after closing cookie popup",
			error,
		);
	}
};
